/**
 * Features - Section for displaying feature cards
 * Replaces SectionWithCards with a simplified, content-focused API
 */

import React from 'react'
import { Box, Container, Typography, Grid } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import TextBlock from '../blocks/TextBlock'
import CardBlock from '../blocks/CardBlock'

interface FeatureItem {
	title: string
	content?: string
	listItems?: string[]
	icon?: string
	image?: string
	href?: string
}

interface FeaturesProps {
	title?: string | React.ReactNode
	titleStyles?: object
	description?: string
	features: FeatureItem[]
	columns?: 1 | 2 | 3 | 4
	backgroundColor?: 'default' | 'white' | 'transparent'
}

const Features: React.FC<FeaturesProps> = ({ title, titleStyles, description, features, columns = 3, backgroundColor = 'default' }) => {
	const theme = useTheme()

	const getBackgroundColor = () => {
		switch (backgroundColor) {
			case 'white':
				return theme.palette.common.white
			case 'transparent':
				return 'transparent'
			case 'default':
				return theme.palette.mode === 'dark' ? theme.palette.custom?.main : theme.palette.grey[50]
			default:
				return theme.palette.mode === 'dark' ? theme.palette.custom?.main : theme.palette.grey[50]
		}
	}

	return (
		<Box
			sx={{
				width: '100%',
				backgroundColor: getBackgroundColor()
			}}
		>
			<Container
				component="section"
				sx={{
					pt: { md: 2, xs: 1.5 },
					pb: { md: 10, xs: 2.5 },
					height: '100%',
					backgroundColor: getBackgroundColor()
				}}
			>
				{(title || description) && (
					<Box sx={{ textAlign: 'center', mb: 4, ...titleStyles }}>
						{title && (
							<Typography
								variant="h2"
								sx={{
									textAlign: 'center',
									mb: description ? 2 : 0,
									mx: 'auto',
									maxWidth: '100%',
									...titleStyles
								}}
							>
								{title}
							</Typography>
						)}
						{description && (
							<TextBlock content={description} align="center" spacing="normal" maxWidth={{ xs: '100%', md: '50%' }} />
						)}
					</Box>
				)}

				<Grid container justifyContent="center" spacing={2}>
					{features.map((feature, index) => (
						<Grid item xs={12} sm={6} md={columns === 1 ? 12 : columns === 2 ? 6 : 4} key={index}>
							<CardBlock
								title={feature.title}
								content={feature.content}
								listItems={feature.listItems}
								icon={feature.icon}
								image={feature.image}
								backgroundColor="white"
								hover={true}
							/>
						</Grid>
					))}
				</Grid>
			</Container>
		</Box>
	)
}

export default Features
